import { useCallback } from 'react';
import * as Draggable from '@givebutter/primitives/draggable';
import { useToast } from '@givebutter/primitives/toast';
import {
    DropTarget,
    NO_TICKETS_MESSAGE,
    TABLE_NOT_FOUND_MESSAGE,
    TICKETS_NOT_FOUND_MESSAGE,
    TICKET_NOT_FOUND_MESSAGE,
    UNSEATED_GROUP_ID,
} from 'assets/js/pages/campaign-settings/campaign-event/table-seating/constants';
import { containerDropReducer } from 'assets/js/pages/campaign-settings/campaign-event/table-seating/reducers/containerDrop';
import { DropType } from 'assets/js/pages/campaign-settings/campaign-event/table-seating/reducers/dropTypes';
import { emptySeatReducer } from 'assets/js/pages/campaign-settings/campaign-event/table-seating/reducers/emptySeat';
import { determineDropActionType } from 'assets/js/pages/campaign-settings/campaign-event/table-seating/reducers/tableSeating';
import { ticketDropReducer } from 'assets/js/pages/campaign-settings/campaign-event/table-seating/reducers/ticketDrop';
import {
    ActionInstruction,
    ActionPayloadMap,
    DropAction,
    StoreAction,
} from 'assets/js/pages/campaign-settings/campaign-event/table-seating/reducers/types';
import { useTableSeatingStore } from 'assets/js/pages/campaign-settings/campaign-event/table-seating/store';
import { DropTargetPayload } from 'assets/js/pages/campaign-settings/campaign-event/table-seating/types';

const useTableDragDrop = () => {
    const toast = useToast();

    const {
        tickets,
        seatingGroups,
        dragState,
        setDragState,
        selectedItems,
        seatGuest,
        unseatGuest,
        swapGuests,
        reshuffleAndSeat,
        takeSnapshot,
    } = useTableSeatingStore(state => ({
        tickets: state.tickets,
        seatingGroups: state.seatingGroups,
        dragState: state.dragState,
        setDragState: state.setDragState,
        selectedItems: state.selectedItems,
        seatGuest: state.seatGuest,
        unseatGuest: state.unseatGuest,
        swapGuests: state.swapGuests,
        reshuffleAndSeat: state.reshuffleAndSeat,
        takeSnapshot: state.takeSnapshot,
    }));

    const processDropAction = useCallback(
        (dropAction: DropAction): ActionInstruction => {
            const state = { tickets, seatingGroups };
            const dropActionType = determineDropActionType(dropAction.payload.overData);

            const reducerMap: Record<string, typeof emptySeatReducer> = {
                [DropType.DROP_ON_EMPTY_SEAT]: emptySeatReducer,
                [DropType.DROP_TICKET]: ticketDropReducer,
                [DropType.DROP_ON_CONTAINER]: containerDropReducer,
            };

            const reducer = reducerMap[dropActionType];
            if (!reducer) {
                return {
                    action: StoreAction.SHOW_ERROR,
                    payload: { message: 'Invalid drop action' },
                };
            }

            return reducer(state, dropAction);
        },
        [tickets, seatingGroups],
    );

    const validateSeatGuest = useCallback(
        (payload: ActionPayloadMap[StoreAction.SEAT_GUEST]): string | null => {
            const { ticketId, tableId } = payload;
            const ticket = tickets[ticketId];
            const table = seatingGroups[tableId];

            if (!ticket) {
                return TICKET_NOT_FOUND_MESSAGE;
            }
            if (!table && tableId !== UNSEATED_GROUP_ID) {
                return TABLE_NOT_FOUND_MESSAGE;
            }
            return null;
        },
        [tickets, seatingGroups],
    );

    const validateSwapGuests = useCallback(
        (payload: ActionPayloadMap[StoreAction.SWAP_GUESTS]): string | null => {
            const { ticketId1, ticketId2 } = payload;
            const ticket1 = tickets[ticketId1];
            const ticket2 = tickets[ticketId2];

            if (!ticket1 || !ticket2) {
                return TICKETS_NOT_FOUND_MESSAGE;
            }
            return null;
        },
        [tickets],
    );

    const validateReshuffleAndSeat = useCallback(
        (payload: ActionPayloadMap[StoreAction.RESHUFFLE_AND_SEAT]): string | null => {
            const { ticketIds, tableId } = payload;

            if (!ticketIds || ticketIds.length === 0) {
                return NO_TICKETS_MESSAGE;
            }

            const invalidTickets = ticketIds.filter((id: string) => !tickets[id]);
            if (invalidTickets.length > 0) {
                return TICKETS_NOT_FOUND_MESSAGE;
            }

            if (tableId !== UNSEATED_GROUP_ID && !seatingGroups[tableId]) {
                return TABLE_NOT_FOUND_MESSAGE;
            }

            return null;
        },
        [tickets, seatingGroups],
    );

    const dispatchAction = useCallback(
        <T extends StoreAction>(action: T, payload: ActionPayloadMap[T]) => {
            switch (action) {
                case StoreAction.SEAT_GUEST: {
                    const seatGuestPayload = payload as ActionPayloadMap[StoreAction.SEAT_GUEST];
                    const error = validateSeatGuest(seatGuestPayload);
                    if (error) {
                        toast.error(error);
                        return;
                    }
                    seatGuest(
                        seatGuestPayload.ticketId,
                        seatGuestPayload.tableId,
                        seatGuestPayload.seatNumber,
                    );
                    break;
                }

                case StoreAction.UNSEAT_GUEST: {
                    const unseatPayload = payload as ActionPayloadMap[StoreAction.UNSEAT_GUEST];
                    const ticket = tickets[unseatPayload.ticketId];
                    if (!ticket || ticket.seating_group_id === UNSEATED_GROUP_ID) return;
                    unseatGuest(unseatPayload.ticketId);
                    break;
                }

                case StoreAction.UNSEAT_MULTIPLE: {
                    const unseatMultiplePayload =
                        payload as ActionPayloadMap[StoreAction.UNSEAT_MULTIPLE];
                    unseatMultiplePayload.ticketIds.forEach(ticketId => {
                        const ticket = tickets[ticketId];
                        if (ticket && ticket.seating_group_id !== UNSEATED_GROUP_ID) {
                            unseatGuest(ticketId);
                        }
                    });
                    break;
                }

                case StoreAction.SWAP_GUESTS: {
                    const swapPayload = payload as ActionPayloadMap[StoreAction.SWAP_GUESTS];
                    const error = validateSwapGuests(swapPayload);
                    if (error) {
                        toast.error(error);
                        return;
                    }
                    swapGuests(swapPayload.ticketId1, swapPayload.ticketId2);
                    break;
                }

                case StoreAction.SHOW_ERROR: {
                    const errorPayload = payload as ActionPayloadMap[StoreAction.SHOW_ERROR];
                    toast.error(errorPayload.message || 'Action not allowed');
                    break;
                }

                case StoreAction.RESHUFFLE_AND_SEAT: {
                    const reshufflePayload =
                        payload as ActionPayloadMap[StoreAction.RESHUFFLE_AND_SEAT];
                    const error = validateReshuffleAndSeat(reshufflePayload);
                    if (error) {
                        toast.error(error);
                        return;
                    }
                    reshuffleAndSeat(
                        reshufflePayload.ticketIds,
                        reshufflePayload.tableId,
                        reshufflePayload.seatNumber || 1,
                    );
                    break;
                }

                default:
                    break;
            }
        },
        [
            toast,
            seatGuest,
            unseatGuest,
            swapGuests,
            reshuffleAndSeat,
            tickets,
            validateSeatGuest,
            validateSwapGuests,
            validateReshuffleAndSeat,
        ],
    );

    const handleDragStart = useCallback(
        (event: Draggable.DragStartEvent) => {
            const { active } = event;
            const dragData = active.data?.current as DropTargetPayload | undefined;

            const hasSelectedItems = selectedItems.size > 0;

            if (dragData?.type === DropTarget.GROUP && dragData.ticketIds) {
                const multiSelectData: DropTargetPayload = {
                    type: DropTarget.MULTI_SELECTION,
                    ticketIds: dragData.ticketIds,
                };
                setDragState(active.id.toString(), multiSelectData);
                return;
            }

            const shouldUseMultiSelection =
                dragData?.type === DropTarget.TICKET &&
                dragData.ticketId &&
                selectedItems.has(dragData.ticketId);

            if (hasSelectedItems && shouldUseMultiSelection) {
                const selectedTicketIds = Array.from(selectedItems);
                const multiSelectData: DropTargetPayload = {
                    type: DropTarget.MULTI_SELECTION,
                    ticketIds: selectedTicketIds,
                };
                setDragState(active.id.toString(), multiSelectData);
            } else {
                setDragState(active.id.toString(), dragData || null);
            }
        },
        [setDragState, selectedItems],
    );

    const processAndDispatch = useCallback(
        (dropAction: DropAction) => {
            const actionInstruction = processDropAction(dropAction);

            if (actionInstruction.action !== StoreAction.SHOW_ERROR) {
                takeSnapshot();
            }

            dispatchAction(actionInstruction.action, actionInstruction.payload);
        },
        [processDropAction, dispatchAction, takeSnapshot],
    );

    const dispatch = useCallback(
        (instruction: ActionInstruction | DropAction) => {
            if ('type' in instruction) {
                processAndDispatch(instruction);
            } else {
                dispatchAction(instruction.action, instruction.payload);
            }
        },
        [processAndDispatch, dispatchAction],
    );

    const handleDragEnd = useCallback(
        (event: Draggable.DragEndEvent) => {
            const { active, over } = event;

            if (!over) {
                return;
            }

            const dropData = over.data?.current as DropTargetPayload | undefined;
            const activeData =
                dragState.activeData || (active.data?.current as DropTargetPayload | undefined);

            if (!activeData || !dropData) {
                return;
            }

            const isMultiSelection = activeData.type === DropTarget.MULTI_SELECTION;
            const shouldUseSelectedItems =
                selectedItems.size > 0 &&
                activeData.type === DropTarget.TICKET &&
                activeData.ticketId &&
                selectedItems.has(activeData.ticketId);

            const multiSelectionTickets = activeData.ticketIds || [];
            const singleSelectionTickets = shouldUseSelectedItems ? Array.from(selectedItems) : [];

            const selectedTicketIds = isMultiSelection
                ? multiSelectionTickets
                : singleSelectionTickets;

            const dropAction: DropAction = {
                type: determineDropActionType(dropData),
                payload: {
                    activeData,
                    overData: dropData,
                    selectedTicketIds,
                },
            };

            dispatch(dropAction);
        },
        [dragState.activeData, selectedItems, dispatch],
    );

    const handleDragCancel = useCallback(() => {
        setDragState(null, null);
    }, [setDragState]);

    return {
        handleDragStart,
        handleDragEnd,
        handleDragCancel,
    };
};

export { useTableDragDrop };
